import React from "react";
import { FiMapPin, FiArrowRight, FiCheck, FiStar, FiX, FiEdit2, FiUsers, FiClock } from "react-icons/fi";
import { Avatar, Badge, Button } from "../ui";
import { formatSmartTimestamp } from "../../utils/format";

// Maps an application status to how it reads on a card — once a user has
// applied, this always reflects their real status instead of a
// re-clickable "Apply" affordance that could submit a duplicate.
const STATUS_CONFIG = {
  pending: { label: "Applied", icon: FiCheck, className: "text-slate-500" },
  shortlisted: { label: "Shortlisted", icon: FiStar, className: "text-primary-600 font-medium" },
  accepted: { label: "Accepted", icon: FiCheck, className: "text-emerald-600 font-medium" },
  rejected: { label: "Not selected", icon: FiX, className: "text-slate-400" },
};

const DAY_MS = 24 * 60 * 60 * 1000;

export default function JobCard({ job, status, isAdmin, onView, onManage }) {
  const deadlineDate = job.deadline ? new Date(job.deadline) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isClosed = deadlineDate && deadlineDate < today;
  const daysUntilClose = deadlineDate ? Math.round((deadlineDate - today) / DAY_MS) : null;
  const closingSoon = !isClosed && daysUntilClose !== null && daysUntilClose <= 3;

  const statusConfig = status ? STATUS_CONFIG[status] : null;

  return (
    <div
      onClick={() => onView(job)}
      className="group flex cursor-pointer flex-col rounded-xl border border-slate-100 bg-white p-5 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-card"
    >
      {/* Urgency badge — the one place a card earns extra visual weight,
          reserved for when it's actually meaningful (closing imminently or
          already closed). A distant or missing deadline stays quiet rather
          than every card competing for the same attention. */}
      {(isClosed || closingSoon) && (
        <div className="mb-3 flex justify-end">
          <Badge variant={isClosed ? "red" : "amber"}>
            <FiClock className="h-3 w-3" />
            {isClosed ? "Closed" : daysUntilClose === 0 ? "Closes today" : `Closes in ${daysUntilClose}d`}
          </Badge>
        </div>
      )}

      <div className="flex items-start gap-3">
        <Avatar name={job.company || job.title} size="md" />
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-slate-900 group-hover:text-primary-600">
            {job.title}
          </h3>
          <p className="mt-0.5 truncate text-sm text-slate-500">{job.company}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-400">
        {job.location && (
          <span className="inline-flex items-center gap-1">
            <FiMapPin className="h-3.5 w-3.5" /> {job.location}
          </span>
        )}
        <span>Posted {formatSmartTimestamp(job.createdAt)}</span>
        {isAdmin && typeof job.applicantCount === "number" && (
          <span className="inline-flex items-center gap-1">
            <FiUsers className="h-3.5 w-3.5" /> {job.applicantCount} applicant{job.applicantCount === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-slate-50 pt-4">
        {isAdmin ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => { e.stopPropagation(); onManage(job); }}
            className="w-full justify-center"
          >
            <FiEdit2 className="h-3.5 w-3.5" /> Manage
          </Button>
        ) : statusConfig ? (
          <span className={`inline-flex items-center gap-1.5 text-sm ${statusConfig.className}`}>
            <statusConfig.icon className="h-3.5 w-3.5" /> {statusConfig.label}
          </span>
        ) : isClosed ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-400">
            <FiX className="h-3.5 w-3.5" /> Applications closed
          </span>
        ) : (
          <Button size="sm" onClick={(e) => { e.stopPropagation(); onView(job); }} className="w-full justify-center">
            Apply now <FiArrowRight className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
